import { useEffect, useState } from "react";
import { Bell, Download, Loader2, Mail, Moon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { COPPER } from "./VestaAuth";

const time = "w-16 text-center rounded-lg border border-neutral-800 bg-neutral-950 px-2 py-1 font-mono text-xs text-white outline-none focus:border-[#C17F59]";

function Riga({ t, v, set }) {
  return (
    <div className={`flex items-center gap-3 rounded-xl border border-neutral-800 p-3 transition-opacity ${t.attivo_globale ? "" : "opacity-40"}`} data-testid={`com-tipo-${t.key}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-semibold text-white">{t.titolo}{t.urgente && <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-red-300">urgente</span>}</div>
        <div className="text-[11px] leading-snug text-neutral-500">{t.attivo_globale ? t.desc : t.sospeso_partner ? "Sospesa da Torri" : "Sospesa da Torri per tutta la rete"}</div>
      </div>
      <label className="flex shrink-0 items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500" title={t.urgente ? "Email subito" : "Email nel riepilogo giornaliero"}>
        <Mail className="h-3.5 w-3.5" /><Switch checked={v.on && v.email} disabled={!v.on} onCheckedChange={(x) => set({ ...v, email: x })} data-testid={`com-email-${t.key}`} />
      </label>
      <Switch checked={v.on} onCheckedChange={(x) => set({ ...v, on: x })} data-testid={`com-on-${t.key}`} />
    </div>
  );
}

function ReportMensili() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/vesta/report-mensili").then((r) => setRows(r.data.report)).catch(() => {}); }, []);
  if (!rows.length) return null;
  const ym = rows[0].ym;
  const pdf = async (x) => {
    const w = window.open("", "_blank");
    try { const r = await api.get(`/vesta/report-mensili/${x.id}/pdf`, { responseType: "blob" }); w.location.href = URL.createObjectURL(r.data); } catch { w?.close(); toast.error("PDF non disponibile"); }
  };
  return (
    <div className="space-y-1.5 rounded-xl border border-neutral-800 p-3" data-testid="com-report-mensili">
      <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: COPPER }}>Report mensili {ym} · da inoltrare ai clienti</div>
      {rows.filter((x) => x.ym === ym).map((x) => (
        <button key={x.id} type="button" onClick={() => pdf(x)} className="flex w-full items-center gap-2 rounded-lg px-2 py-1 text-left text-xs text-neutral-200 hover:bg-white/5" data-testid={`com-report-${x.id}`}>
          <Download className="h-3.5 w-3.5 text-[#C17F59]" /><span className="flex-1 truncate">{x.plant_nome}</span><span className="text-neutral-500">{x.cliente_email || ""}</span>
        </button>))}
    </div>
  );
}

export default function ComunicazioniDialog({ open, onOpenChange }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/vesta/comunicazioni/prefs").then((r) => setD(r.data)).catch(() => toast.error("Impostazioni non disponibili")); }, [open]);
  const p = d?.prefs;
  const setTipo = (k, v) => setD({ ...d, prefs: { ...p, tipi: { ...p.tipi, [k]: v } } });
  const setSil = (v) => setD({ ...d, prefs: { ...p, silenzio: { ...p.silenzio, ...v } } });
  const save = async () => {
    setBusy(true);
    try { await api.put("/vesta/comunicazioni/prefs", p); toast.success("Preferenze salvate"); onOpenChange(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto border-neutral-800 bg-neutral-950 text-white" data-testid="com-prefs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl" style={{ color: COPPER }}><Bell className="h-6 w-6" />Comunicazioni Vesta</DialogTitle>
          <DialogDescription className="text-xs text-neutral-400">Scegli quali avvisi ricevere sui tuoi impianti. Li trovi nella campanella e Vesta te li dice a voce; con la busta li ricevi anche per email (urgenti subito, le altre nel riepilogo del mattino).</DialogDescription>
        </DialogHeader>
        {!p ? <Loader2 className="h-5 w-5 animate-spin" style={{ color: COPPER }} /> : (
          <div className="space-y-2">
            <div className="flex justify-end gap-5 pr-1 text-[9px] font-bold uppercase tracking-widest text-neutral-500"><span>Anche email</span><span>Attiva</span></div>
            {[...new Set(d.tipi.map((t) => t.gruppo))].map((g) => (
              <div key={g} className="space-y-2" data-testid={`com-gruppo-${g}`}>
                <div className="flex items-center gap-2 pt-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: COPPER }}><span className="flex-1">{g}</span>
                  <span className="text-neutral-500">Tutto il gruppo</span>
                  <Switch checked={d.tipi.filter((t) => t.gruppo === g).some((t) => p.tipi[t.key].on)} data-testid={`com-gruppo-switch-${g}`}
                    onCheckedChange={(x) => setD({ ...d, prefs: { ...p, tipi: { ...p.tipi, ...Object.fromEntries(d.tipi.filter((t) => t.gruppo === g).map((t) => [t.key, { ...p.tipi[t.key], on: x }])) } } })} /></div>
                {d.tipi.filter((t) => t.gruppo === g).map((t) => <Riga key={t.key} t={t} v={p.tipi[t.key]} set={(v) => setTipo(t.key, v)} />)}
              </div>))}
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-neutral-800 p-3" data-testid="com-silenzio">
              <Moon className="h-4 w-4 text-neutral-400" /><span className="flex-1 text-sm font-semibold">Orario di silenzio <span className="block text-[11px] font-normal text-neutral-500">Niente email in questa fascia: arrivano nel riepilogo successivo.</span></span>
              <input type="text" inputMode="numeric" maxLength={5} placeholder="20:00" value={p.silenzio.dal} onChange={(e) => setSil({ dal: e.target.value })} className={time} data-testid="com-silenzio-dal" />
              <span className="text-xs text-neutral-500">→</span>
              <input type="text" inputMode="numeric" maxLength={5} placeholder="07:30" value={p.silenzio.al} onChange={(e) => setSil({ al: e.target.value })} className={time} data-testid="com-silenzio-al" />
              <Switch checked={p.silenzio.attivo} onCheckedChange={(x) => setSil({ attivo: x })} data-testid="com-silenzio-on" />
            </div>
            <ReportMensili />
            <div className="flex justify-end pt-2">
              <button type="button" onClick={save} disabled={busy} className="rounded-full px-5 py-2 text-xs font-bold uppercase tracking-widest text-black transition-opacity hover:opacity-90 disabled:opacity-50" style={{ background: COPPER }} data-testid="com-prefs-save">{busy ? "Salvo…" : "Salva"}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
